/**
 * Notification Metrics Dashboard
 * 
 * Summary cards and charts for notification platform monitoring.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@etherisc/ui-kit';
import { 
  MailIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  AlertTriangleIcon
} from 'lucide-react';

interface Notification {
  id: string;
  status: string;
  createdAt: string;
  sentAt?: string;
}

interface MetricsData {
  totalSent24h: number;
  deliveryRate: number;
  bounceRate: number;
  queueDepth: number;
  dlqCount: number;
  avgLatency: number;
}

export function NotificationMetrics() {
  const [metrics, setMetrics] = useState<MetricsData>({
    totalSent24h: 0,
    deliveryRate: 0,
    bounceRate: 0,
    queueDepth: 0,
    dlqCount: 0,
    avgLatency: 0
  });
  const [loading, setLoading] = useState(true);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      
      // Fetch notifications from last 24h
      const response = await fetch('/api/notifications/search?limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to load metrics');

      const data = await response.json();
      const notifications = (data.data.notifications || []) as Notification[];

      // Calculate metrics
      const now = Date.now();
      const last24h = notifications.filter((n) => 
        new Date(n.createdAt).getTime() > now - 24 * 60 * 60 * 1000
      );

      // Count notifications by status
      const sent = last24h.filter((n) => 
        ['SENT', 'DELIVERED'].includes(n.status)
      );
      
      const bounced = last24h.filter((n) => n.status === 'BOUNCED');
      const queued = notifications.filter((n) => n.status === 'QUEUED');
      const dlq = notifications.filter((n) => n.status === 'DLQ');

      // Calculate delivery rate
      // SENT + DELIVERED = successfully handed to provider
      // Total = SENT + DELIVERED + BOUNCED
      const total = sent.length + bounced.length;
      const successful = sent.length;

      setMetrics({
        totalSent24h: sent.length,
        deliveryRate: total > 0 ? (successful / total) * 100 : 0,
        bounceRate: total > 0 ? (bounced.length / total) * 100 : 0,
        queueDepth: queued.length,
        dlqCount: dlq.length,
        avgLatency: 0 // TODO: Calculate from sentAt - createdAt
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  const MetricCard = ({ 
    title, 
    value, 
    icon, 
    trend, 
    trendLabel,
    variant = 'neutral'
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    trend?: 'up' | 'down';
    trendLabel?: string;
    variant?: 'success' | 'warning' | 'error' | 'neutral';
  }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className={`text-3xl font-bold mt-2 ${
              variant === 'success' ? 'text-green-600' :
              variant === 'warning' ? 'text-yellow-600' :
              variant === 'error' ? 'text-red-600' :
              'text-gray-900'
            }`}>
              {loading ? '...' : value}
            </p>
            {trendLabel && (
              <p className={`text-sm mt-1 flex items-center gap-1 ${
                trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {trend === 'up' && '↑'}
                {trend === 'down' && '↓'}
                {trendLabel}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${
            variant === 'success' ? 'bg-green-100' :
            variant === 'warning' ? 'bg-yellow-100' :
            variant === 'error' ? 'bg-red-100' :
            'bg-gray-100'
          }`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <MetricCard
        title="Sent (24h)"
        value={metrics.totalSent24h}
        icon={<MailIcon size={24} className="text-blue-600" />}
        variant="neutral"
      />
      
      <MetricCard
        title="Delivery Rate"
        value={`${metrics.deliveryRate.toFixed(1)}%`}
        icon={<CheckCircleIcon size={24} className="text-green-600" />}
        variant={metrics.deliveryRate > 95 ? 'success' : metrics.deliveryRate > 85 ? 'warning' : 'error'}
        trend={metrics.deliveryRate > 95 ? 'up' : 'down'}
        trendLabel={metrics.deliveryRate > 95 ? 'Excellent' : 'Needs attention'}
      />
      
      <MetricCard
        title="Bounce Rate"
        value={`${metrics.bounceRate.toFixed(1)}%`}
        icon={<XCircleIcon size={24} className="text-red-600" />}
        variant={metrics.bounceRate < 2 ? 'success' : metrics.bounceRate < 5 ? 'warning' : 'error'}
        trend={metrics.bounceRate < 2 ? 'down' : 'up'}
        trendLabel={metrics.bounceRate < 2 ? 'Good' : 'High'}
      />
      
      <MetricCard
        title="Queue Depth"
        value={metrics.queueDepth}
        icon={<ClockIcon size={24} className="text-gray-600" />}
        variant={metrics.queueDepth < 10 ? 'success' : metrics.queueDepth < 50 ? 'warning' : 'error'}
      />
      
      {metrics.dlqCount > 0 && (
        <div className="lg:col-span-4">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertTriangleIcon size={24} className="text-red-600" />
                <div>
                  <p className="font-semibold text-red-900">
                    {metrics.dlqCount} notification{metrics.dlqCount > 1 ? 's' : ''} in DLQ
                  </p>
                  <p className="text-sm text-red-700">
                    Failed notifications require attention. Check "DLQ Only" filter to review and retry.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

